import { memo, forwardRef, useCallback, useMemo } from 'react';
import { FormHelperText, Box } from '@mui/material';
import { SwitchComponentProps } from './Switch.types';
import { StyledSwitch, StyledFormControlLabel } from './Switch.styles';

/**
 * Switch Component
 *
 * Professional toggle switch component following the green/teal design system.
 * Fully memoized for optimal performance in large ERP applications.
 *
 * @example
 * ```tsx
 * // Basic switch
 * <Switch
 *   label="Enable notifications"
 *   checked={enabled}
 *   onChange={(e, checked) => setEnabled(checked)}
 * />
 *
 * // With validation
 * <Switch
 *   label="I agree to terms"
 *   required
 *   error={!!errors.agreement}
 *   errorMessage={errors.agreement}
 * />
 *
 * // With helper text
 * <Switch
 *   label="Dark mode"
 *   helperText="Toggle between light and dark themes"
 * />
 * ```
 */
export const Switch = memo(
  forwardRef<HTMLButtonElement, SwitchComponentProps>(function Switch(
    {
      size = 'md',
      label,
      helperText,
      validationState = 'default',
      error = false,
      errorMessage,
      successMessage,
      checked,
      defaultChecked,
      disabled = false,
      required = false,
      onChange,
      value,
      name,
      id,
      className,
      'data-testid': dataTestId,
      labelPlacement = 'end',
      ...props
    },
    ref
  ) {
    // Determine validation state
    const finalValidationState = useMemo(() => {
      if (error || errorMessage) return 'error';
      if (successMessage) return 'success';
      return validationState;
    }, [error, errorMessage, successMessage, validationState]);

    // Determine helper text
    const finalHelperText = useMemo(() => {
      if (errorMessage) return errorMessage;
      if (successMessage) return successMessage;
      return helperText;
    }, [errorMessage, successMessage, helperText]);

    // Memoize change handler
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>, checkedValue: boolean) => {
        onChange?.(event, checkedValue);
      },
      [onChange]
    );

    // Create the switch element
    const switchElement = (
      <StyledSwitch
        ref={ref}
        switchSize={size}
        validationState={finalValidationState}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        value={value}
        name={name}
        id={id}
        inputProps={
          {
            ...props.inputProps,
            'data-testid': dataTestId,
          } as any
        }
        {...props}
      />
    );

    // If there's a label, wrap with FormControlLabel
    const content = label ? (
      <StyledFormControlLabel
        switchSize={size}
        validationState={finalValidationState}
        control={switchElement}
        label={label}
        disabled={disabled}
        labelPlacement={labelPlacement}
        className={className}
      />
    ) : (
      switchElement
    );

    // If there's helper text, wrap in a box
    if (finalHelperText) {
      return (
        <Box>
          {content}
          <FormHelperText
            error={finalValidationState === 'error'}
            sx={{ ml: label && labelPlacement === 'end' ? '58px' : 0 }}
          >
            {finalHelperText}
          </FormHelperText>
        </Box>
      );
    }

    return content;
  })
);

Switch.displayName = 'Switch';

export default Switch;
